import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { ContentProvider } from './ContentContext';
import { CatalogProvider } from './CatalogContext';
import { AuthProvider } from './context/AuthContext';
import { LocationProvider, useLocation } from './context/LocationContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';

import TopStrip from './components/TopStrip';
import Header from './components/Header';
import Footer from './components/Footer';
import Floaters from './components/Floaters';
import ChatWidget from './components/ChatWidget';
import Splash from './components/Splash';
import { OfferPopup, SamplePopup, EnquiryModal } from './components/Modals';
import { ScrollManager } from './components/Common';
import { MetaApi } from './api';

import Home from './pages/Home';
import BrandPage from './pages/BrandPage';
import CategoryPage from './pages/CategoryPage';
import ProductDetails from './pages/ProductDetails';
import ProductsPage from './pages/ProductsPage';
import PackDetails from './pages/PackDetails';
import Recipes from './pages/Recipes';
import Occasions from './pages/Occasions';
import CompanyProfile from './pages/CompanyProfile';
import VisionMission from './pages/VisionMission';
import Infrastructure from './pages/Infrastructure';
import QualityAssurance from './pages/QualityAssurance';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';

import Login from './pages/Login';
import CartPage from './pages/CartPage';
import WishlistPage from './pages/WishlistPage';
import AccountPage from './pages/AccountPage';
import OrderDetails from './pages/OrderDetails';
import OrderSuccess from './pages/OrderSuccess';
import RewardsPage from './pages/RewardsPage';

const SEEN_KEY = 'gt_launched';

// Splash → site. Browsing is open to everyone; the pincode is only asked for on
// the shop, where prices and delivery actually depend on it.
function Launch({ children }) {
  const { ready } = useLocation();
  const [phase, setPhase] = useState(() =>
    sessionStorage.getItem(SEEN_KEY) ? 'done' : 'splash'
  );

  useEffect(() => {
    if (phase === 'done') sessionStorage.setItem(SEEN_KEY, '1');
  }, [phase]);

  if (!ready) return null;

  if (phase === 'splash') return <Splash onDone={() => setPhase('done')} />;

  return children;
}

function Shell() {
  const [enquiry, setEnquiry] = useState(false);
  const [sample, setSample] = useState(null); // { enabled, image, link } from admin
  const [sampleOpen, setSampleOpen] = useState(false);

  // Admin-uploaded "Get Free Sample" popup, from Settings.
  useEffect(() => {
    MetaApi.config().then((d) => setSample(d.samplePopup || null)).catch(() => {});
  }, []);

  const hasSample = !!(sample?.enabled && sample?.image);

  // "Get Free Sample" shows the admin popup image when one is set, otherwise it
  // falls back to the enquiry form.
  const onGetSample = () => (hasSample ? setSampleOpen(true) : setEnquiry(true));

  return (
    <Launch>
      <ScrollManager />
      <TopStrip />
      <Header onEnquiry={onGetSample} />

      <main>
        <Routes>
          <Route path="/" element={<Home />} />

          {/* Shop */}
          <Route path="/login" element={<Login />} />
          <Route path="/cart" element={<CartPage />} />
          {/* Checkout now lives inside the cart (address + coupon + payment). */}
          <Route path="/checkout" element={<Navigate to="/cart" replace />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/order/:id" element={<OrderDetails />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/product/:slug" element={<ProductDetails />} />
          <Route path="/pack/:code" element={<PackDetails />} />
          <Route path="/category/:slug" element={<CategoryPage />} />

          {/* Marketing */}
          <Route path="/recipes" element={<Recipes />} />
          <Route path="/occasions" element={<Occasions />} />
          <Route path="/company-profile" element={<CompanyProfile />} />
          <Route path="/vision-mission" element={<VisionMission />} />
          <Route path="/infrastructure" element={<Infrastructure />} />
          <Route path="/quality-assurance" element={<QualityAssurance />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/delete" element={<DeleteAccount />} />

          {/* Brand pages last: static routes above always outrank this. */}
          <Route path="/:slug" element={<BrandPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
      <Floaters />
      <ChatWidget />
      <OfferPopup />
      <SamplePopup open={sampleOpen} image={sample?.image} link={sample?.link} onClose={() => setSampleOpen(false)} />
      <EnquiryModal open={enquiry} onClose={() => setEnquiry(false)} />
    </Launch>
  );
}

export default function App() {
  return (
    <ContentProvider>
      <AuthProvider>
        <LocationProvider>
          <CatalogProvider>
            <CartProvider>
              <WishlistProvider>
                <BrowserRouter>
                  <Shell />
                </BrowserRouter>
              </WishlistProvider>
            </CartProvider>
          </CatalogProvider>
        </LocationProvider>
      </AuthProvider>
    </ContentProvider>
  );
}
